import { API, getAuth } from '../api';
import {
  normalizeActivity,
  normalizeActivityDate,
  normalizeActivityTime,
  getLocalTimeString,
} from '../utils/activityStore';



function toUtcDateTimeParts(localDate, localTime) {
  if (!localDate || !localTime) return { date: localDate, time: localTime };
  const d = new Date(`${localDate}T${localTime}:00`);
  if (isNaN(d.getTime())) return { date: localDate, time: localTime };
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mi = String(d.getUTCMinutes()).padStart(2, '0');
  return { date: `${yyyy}-${mm}-${dd}`, time: `${hh}:${mi}` };
}

function toLocalDateTimeParts(utcDate, utcTime) {
  if (!utcDate || !utcTime) return { date: utcDate, time: utcTime };
  const d = new Date(`${utcDate}T${utcTime}:00Z`);
  if (isNaN(d.getTime())) return { date: utcDate, time: utcTime };
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return { date: `${yyyy}-${mm}-${dd}`, time: `${hh}:${mi}` };
}

function buildBackendPayload(activity, userId) {
  const safeDate = normalizeActivityDate(activity.date);
  const safeTime = normalizeActivityTime(activity.time, getLocalTimeString());
  const utc = toUtcDateTimeParts(safeDate, safeTime);
 
  return {
    name: activity.type,
    category: activity.category,
    durationMin: activity.durationMin,
    intensity: activity.intensity || null,
    calories: activity.calories || null,
    note: activity.note || null,
    date: utc.date,
    time: utc.time,
    userId,
  };
}

function sortByNewest(activities) {
  return [...activities].sort(
    (a, b) =>
      (b.date || '').localeCompare(a.date || '') ||
      (b.time || '').localeCompare(a.time || ''),
  );
}

export function sortActivitiesByNewest(activities) {
  return sortByNewest(activities);
}

// ─── NETWORK ──────────────────────────────────────────────────────

export async function saveActivityToBackend(activity) {
  const auth = getAuth();
  if (!auth?.token || !auth?.userId || !process.env.REACT_APP_API_BASE_URL) {
    return false;
  }

  // Kullanıcının girdiği date/time'ı önce normalize et (eksikse bugün/şu an),
  // sonra UTC'ye çevirip backend'e öyle yolla.
  const safeDate = normalizeActivityDate(activity.date);
  const safeTime = normalizeActivityTime(activity.time, getLocalTimeString());
  const utc = toUtcDateTimeParts(safeDate, safeTime);

  try {
    const response = await fetch(API.activities(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${auth.token}`,
      },
      credentials: 'include',
      body: JSON.stringify({
        name: activity.type,
        category: activity.category,
        durationMin: activity.durationMin,
        intensity: activity.intensity || null,
        calories: activity.calories || null,
        note: activity.note || null,
        date: utc.date,
        time: utc.time,
        userId: auth.userId,
      }),
    });

    return response.ok;
  } catch (error) {
    console.error('Error saving activity:', error);
    return false;
  }
}

export async function updateActivityOnBackend(activityId, activity) {
  const auth = getAuth();
  if (!auth?.token || !auth?.userId || !process.env.REACT_APP_API_BASE_URL || !activityId) {
    return false;
  }
 
  try {
    const response = await fetch(API.activityById(activityId), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${auth.token}`,
      },
      credentials: 'include',
      body: JSON.stringify(buildBackendPayload(activity, auth.userId)),
    });
 
    return response.ok;
  } catch (error) {
    console.error('Error updating activity:', error);
    return false;
  }
}
export async function fetchActivitiesFromBackend() {
  const auth = getAuth();
  if (!auth?.token || !auth?.userId || !process.env.REACT_APP_API_BASE_URL) {
    return null;
  }

  try {
    const response = await fetch(API.activitiesByUser(auth.userId), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${auth.token}`,
      },
      credentials: 'include',
    });

    if (!response.ok) return null;

    const data = await response.json();
    // Backend UTC döndürüyor → lokal'e çevir → normalizeActivity garanti
    // altına alır (bozuk date/time gelirse de patlamasın).
    return sortByNewest(
      data.map((item) => {
        const rawDate = item.date ?? item.activityDate ?? item.startDate ?? null;
        const rawTime = item.time ?? item.activityTime ?? item.startTime ?? null;
        const local = toLocalDateTimeParts(rawDate, rawTime);
        return normalizeActivity({
          ...item,
          date: local.date,
          time: local.time,
          synced: true,
        });
      }),
    );
  } catch (error) {
    console.error('Error fetching activities:', error);
    return null;
  }
}

export async function fetchLatestActivities(limit = 2) {
  const activities = await fetchActivitiesFromBackend();
  if (!activities) return null;
  return activities.slice(0, limit);
}

export async function deleteActivityFromBackend(activityId) {
  const auth = getAuth();
  if (!auth?.token || !process.env.REACT_APP_API_BASE_URL) {
    return false;
  }

  try {
    const response = await fetch(API.activityById(activityId), {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${auth.token}`,
      },
      credentials: 'include',
    });

    return response.ok;
  } catch (error) {
    console.error('Error deleting activity:', error);
    return false;
  }
}